/**
 * utils/artifactCells.ts — FRONTEND MIRROR of supabase/functions/server/artifactCellsCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 * tests/server/artifactCells_test.ts asserts parity.
 */


import { foldStatus, isLocked } from './configItemLifecycle.ts';

export type ArtifactKind = 'work_type' | 'dashboards' | 'space_architecture' | 'wizard';

export type ArtifactStatus = 'not_started' | 'in_progress' | 'blocked' | 'complete' | 'locked';

export interface GatingIssue {
  message: string;
  actionLink?: string;
  actionLabel?: string;
}

export interface ArtifactCell {
  /** Stable id, e.g. `artifact:work_type:<ciId>` or `artifact:dashboards` */
  id: string;
  kind: ArtifactKind;
  label: string;
  description?: string;
  /** Present for CI-scoped artifacts (work_type, wizard) */
  configItemId?: string;
  wizardId?: string;
  wizardName?: string;
  status: ArtifactStatus;
  /** 0..100 */
  completion: number;
  gatingIssues: GatingIssue[];
  /** Route to open the artifact; absent for wizards (opened in place) */
  openPath?: string;
  completedAt?: string;
  source: 'engagement_config' | 'wizard_session' | 'config_item';
}

export interface ArtifactLinks {
  companyId: string;
  /** Focused engagement / SOW id; null when none is known */
  sowId: string | null;
}

export interface WizardSessionRecord {
  configItemId: string;
  wizardId: string;
  wizardName?: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  currentPage?: number;
  totalPages?: number;
  completedAt?: string;
  updatedAt?: string;
}

/** Minimal config-item shape the predicates need. */
export interface ArtifactConfigItem {
  id: string;
  name: string;
  category?: string;
  status?: string;
  artifact?: {
    kind?: ArtifactKind;
    wizardId?: string;
    wizardName?: string;
    status?: ArtifactStatus;
    completedAt?: string;
  } | null;
  customQuestions?: Array<{
    id: string;
    grouping?: { isPartOfWizard?: boolean; wizardId?: string; wizardName?: string; gridBehavior?: string };
  }>;
  [key: string]: any;
}

export const WORK_TYPE_CATEGORIES = ['service_requests', 'incidents', 'changes', 'problems'];

// ---------------------------------------------------------------------------
// Work types (port of the gating logic that lived inline in the grid)
// ---------------------------------------------------------------------------

export function workTypeGatingIssues(
  item: ArtifactConfigItem,
  config: any | null | undefined,
  links: ArtifactLinks,
): GatingIssue[] {
  const issues: GatingIssue[] = [];
  if (!config) return issues;

  const sow = links.sowId ?? 'unknown';
  const wtConfig = config.workTypes?.[item.id];
  const isSpaceLocked = !!(config.spaceArchitecture?.locked && config.spaceArchitecture?.status === 'ready_for_build');

  if (!isSpaceLocked) {
    issues.push({
      message: 'Space Architecture not committed',
      actionLink: `/clients/${links.companyId}/space-review`,
      actionLabel: 'Review Spaces',
    });
  }

  if (!wtConfig) {
    issues.push({
      message: 'Not configured in Engagement',
      actionLink: `/sow/${sow}/work-types/${item.id}`,
      actionLabel: 'Configure Work Type',
    });
    return issues;
  }

  if (!wtConfig.workflowConfig?.workflowTemplateId) {
    issues.push({ message: 'Workflow not configured', actionLink: `/sow/${sow}/work-types/${item.id}/workflow`, actionLabel: 'Configure Workflow' });
  }
  if (!wtConfig.slaConfig) {
    issues.push({ message: 'SLA not configured', actionLink: `/sow/${sow}/work-types/${item.id}/sla`, actionLabel: 'Configure SLA' });
  }
  if (!wtConfig.priorityMatrixConfig) {
    issues.push({ message: 'Priority Matrix not configured', actionLink: `/sow/${sow}/work-types/${item.id}/priority`, actionLabel: 'Configure Priority' });
  }
  if (!wtConfig.slaCalendar) {
    issues.push({ message: 'SLA Calendar not configured', actionLink: `/sow/${sow}/calendars`, actionLabel: 'Configure Calendars' });
  }
  if (!wtConfig.slaPauseConditions) {
    issues.push({ message: 'SLA Pause logic missing', actionLink: `/sow/${sow}/work-types/${item.id}/sla`, actionLabel: 'Configure Pause Logic' });
  }
  if (!wtConfig.channelConfig) {
    issues.push({ message: 'Channels not defined', actionLink: `/sow/${sow}/work-types/${item.id}/channels`, actionLabel: 'Configure Channels' });
  }
  if (!wtConfig.queueConfig || !wtConfig.queueConfig.queues || wtConfig.queueConfig.queues.length === 0) {
    issues.push({ message: 'Queues not configured', actionLink: `/sow/${sow}/queues`, actionLabel: 'Configure Queues' });
  }

  const template = wtConfig.workflowConfig?.workflowTemplateId;

  if (template === 'change-management') {
    if (!wtConfig.workflowConfig.changeTypes || wtConfig.workflowConfig.changeTypes.length === 0) {
      issues.push({ message: 'No Change Models selected', actionLink: `/sow/${sow}/work-types/${item.id}/workflow`, actionLabel: 'Select Models' });
    }
    if (!wtConfig.riskModel) {
      issues.push({ message: 'Risk Model not defined', actionLink: `/sow/${sow}/work-types/${item.id}/risk`, actionLabel: 'Define Risk Model' });
    }
    if (!wtConfig.approvals) {
      issues.push({ message: 'Approval policies not defined', actionLink: `/sow/${sow}/work-types/${item.id}/approvals`, actionLabel: 'Define Approvals' });
    }
    if (wtConfig.blackouts === undefined) {
      issues.push({ message: 'Blackout enforcement not decided', actionLink: `/sow/${sow}/work-types/${item.id}/blackouts`, actionLabel: 'Configure Blackouts' });
    }
  }

  if (template === 'problem-management') {
    const problemConfig = wtConfig.problemConfig;
    if (problemConfig?.enableProblemManagement === undefined) {
      issues.push({ message: 'Enablement decision required', actionLink: `/sow/${sow}/work-types/${item.id}`, actionLabel: 'Configure Problem Mgmt' });
    } else if (problemConfig.enableProblemManagement && !problemConfig.rcaRequirement) {
      issues.push({ message: 'RCA Requirement not defined', actionLink: `/sow/${sow}/work-types/${item.id}/rca`, actionLabel: 'Configure RCA' });
    }
  }

  if (template === 'post-incident-review') {
    const incidentWT = Object.values(config.workTypes ?? {}).find(
      (wt: any) => wt?.workflowConfig?.workflowTemplateId === 'incident-management',
    ) as any;
    if (!incidentWT || !incidentWT.locked) {
      issues.push({ message: 'Incident Management must be locked first', actionLink: `/sow/${sow}/work-types`, actionLabel: 'Review Incident Mgmt' });
    }
  }

  return issues;
}

/** The item's lifecycle status as an artifact status: complete once approved, locked at any locked status. */
function statusFromItem(itemStatus: string | undefined): ArtifactStatus {
  const status = foldStatus(itemStatus);
  if (status === 'blocked') return 'blocked';
  if (status === 'approved') return 'complete';
  if (isLocked(status)) return 'locked';
  return itemStatus === 'in_progress' || itemStatus === 'draft' ? 'in_progress' : 'not_started';
}

function completionFromStatus(status: ArtifactStatus): number {
  switch (status) {
    case 'complete':
    case 'locked':
      return 100;
    case 'in_progress':
      return 50;
    case 'blocked':
      return 10;
    default:
      return 0;
  }
}

export function workTypeArtifact(item: ArtifactConfigItem, config: any | null | undefined, links: ArtifactLinks): ArtifactCell {
  const gatingIssues = workTypeGatingIssues(item, config, links);
  let status = statusFromItem(item.status);
  if (gatingIssues.length > 0) status = 'blocked';
  return {
    id: `artifact:work_type:${item.id}`,
    kind: 'work_type',
    label: item.name,
    configItemId: item.id,
    status,
    completion: completionFromStatus(status),
    gatingIssues,
    openPath: `/sow/${links.sowId ?? 'unknown'}/work-types/${item.id}`,
    completedAt: item.completedAt,
    source: 'engagement_config',
  };
}

// ---------------------------------------------------------------------------
// Engagement-level artifacts
// ---------------------------------------------------------------------------

export function dashboardsArtifact(config: any | null | undefined, links: ArtifactLinks): ArtifactCell | null {
  const dash = config?.dashboards;
  if (!dash?.enabled) return null;
  const lockedCount = (dash.definitions ?? []).filter((d: any) => d?.locked).length;
  const requiredCount = dash.selectedPackage === 'basic' ? 3 : 5;
  const gatingIssues: GatingIssue[] = [];
  let status: ArtifactStatus = 'locked';
  if (lockedCount < requiredCount) {
    gatingIssues.push({
      message: `Missing definitions (${lockedCount}/${requiredCount})`,
      actionLink: `/clients/${links.companyId}/sow/${links.sowId ?? 'unknown'}/dashboards`,
      actionLabel: 'Configure Dashboards',
    });
    status = lockedCount === 0 ? 'not_started' : 'in_progress';
  }
  return {
    id: 'artifact:dashboards',
    kind: 'dashboards',
    label: 'Dashboards & Reporting',
    description: `${lockedCount}/${requiredCount} dashboard definitions locked`,
    status,
    completion: Math.min(100, Math.round((lockedCount / requiredCount) * 100)),
    gatingIssues,
    openPath: `/clients/${links.companyId}/sow/${links.sowId ?? 'unknown'}/dashboards`,
    source: 'engagement_config',
  };
}

export function spaceArchitectureArtifact(config: any | null | undefined, links: ArtifactLinks): ArtifactCell | null {
  if (!config) return null;
  const sa = config.spaceArchitecture;
  const gatingIssues: GatingIssue[] = [];
  let status: ArtifactStatus;
  if (!sa) {
    status = 'not_started';
    gatingIssues.push({
      message: 'Space Architecture not committed',
      actionLink: `/clients/${links.companyId}/space-review`,
      actionLabel: 'Review Spaces',
    });
  } else if (sa.locked && sa.status === 'ready_for_build') {
    status = 'locked';
  } else {
    status = 'in_progress';
    gatingIssues.push({
      message: 'Space Architecture not locked',
      actionLink: `/clients/${links.companyId}/space-review`,
      actionLabel: 'Review Spaces',
    });
  }
  return {
    id: 'artifact:space_architecture',
    kind: 'space_architecture',
    label: 'Space Architecture',
    status,
    completion: completionFromStatus(status),
    gatingIssues,
    openPath: `/clients/${links.companyId}/space-review`,
    source: 'engagement_config',
  };
}

// ---------------------------------------------------------------------------
// Wizard artifacts
// ---------------------------------------------------------------------------

export function wizardSessionToStatus(session: WizardSessionRecord | null | undefined): { status: ArtifactStatus; completion: number } {
  if (!session) return { status: 'not_started', completion: 0 };
  if (session.status === 'completed') return { status: 'complete', completion: 100 };
  const total = Number(session.totalPages ?? 0);
  const current = Number(session.currentPage ?? 0);
  const completion = total > 0 ? Math.max(0, Math.min(99, Math.round((current / total) * 100))) : 0;
  return { status: 'in_progress', completion };
}

/** Wizards a CI depends on: an explicit `artifact.wizardId`, or questions that can only be answered in a wizard. */
export function wizardIdsForItem(item: ArtifactConfigItem): Array<{ wizardId: string; wizardName?: string }> {
  const found = new Map<string, string | undefined>();
  if (item.artifact?.kind === 'wizard' && item.artifact.wizardId) {
    found.set(item.artifact.wizardId, item.artifact.wizardName);
  }
  for (const q of item.customQuestions ?? []) {
    const g = q?.grouping;
    if (g?.isPartOfWizard && g.wizardId && g.gridBehavior === 'wizard_only' && !found.has(g.wizardId)) {
      found.set(g.wizardId, g.wizardName);
    }
  }
  return [...found.entries()].map(([wizardId, wizardName]) => ({ wizardId, wizardName }));
}

export function wizardArtifactsForItem(
  item: ArtifactConfigItem,
  sessions: ReadonlyArray<WizardSessionRecord>,
): ArtifactCell[] {
  return wizardIdsForItem(item).map(({ wizardId, wizardName }) => {
    const session = sessions.find((s) => s.configItemId === item.id && s.wizardId === wizardId) ?? null;
    let { status, completion } = wizardSessionToStatus(session);
    // A CI stamped complete by the server wins over a missing/stale session record
    if (item.artifact?.wizardId === wizardId && item.artifact?.status === 'complete') {
      status = 'complete';
      completion = 100;
    }
    return {
      id: `artifact:wizard:${item.id}:${wizardId}`,
      kind: 'wizard',
      label: wizardName ?? session?.wizardName ?? item.name,
      description: `Wizard for ${item.name}`,
      configItemId: item.id,
      wizardId,
      wizardName: wizardName ?? session?.wizardName,
      status,
      completion,
      gatingIssues: [],
      completedAt: session?.completedAt ?? item.artifact?.completedAt,
      source: 'wizard_session',
    };
  });
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

export interface BuildArtifactCellsInput {
  items: ReadonlyArray<ArtifactConfigItem>;
  engagementConfig: any | null | undefined;
  wizardSessions: ReadonlyArray<WizardSessionRecord>;
  links: ArtifactLinks;
  /** Include the one-cell-per-work-type artifacts (default true) */
  includeWorkTypes?: boolean;
  /** Include engagement-level dashboards / space architecture cells (default true) */
  includeEngagementArtifacts?: boolean;
}

/** All shadow cells for a company's grid, engagement-level first, then per CI in input order. */
export function buildArtifactCells(input: BuildArtifactCellsInput): ArtifactCell[] {
  const { items, engagementConfig, wizardSessions, links } = input;
  const includeWorkTypes = input.includeWorkTypes ?? true;
  const includeEngagement = input.includeEngagementArtifacts ?? true;
  const cells: ArtifactCell[] = [];

  if (includeEngagement) {
    const dash = dashboardsArtifact(engagementConfig, links);
    if (dash) cells.push(dash);
    const space = spaceArchitectureArtifact(engagementConfig, links);
    if (space) cells.push(space);
  }

  for (const item of items) {
    if (!item) continue;
    if (includeWorkTypes && item.category && WORK_TYPE_CATEGORIES.includes(item.category)) {
      cells.push(workTypeArtifact(item, engagementConfig, links));
    }
    cells.push(...wizardArtifactsForItem(item, wizardSessions));
  }

  return cells;
}

/** True when a CI is represented by a shadow cell instead of per-question cells. */
export function isWorkTypeItem(item: ArtifactConfigItem): boolean {
  return !!item.category && WORK_TYPE_CATEGORIES.includes(item.category);
}
